"use client";

import { useState } from "react";
import {
  Shirt,
  Users,
  Building2,
  Home,
  Car,
  Sofa,
  Tv2,
  Refrigerator,
  MapPin,
  Phone,
  Mail,
  Bath,
  BedDouble,
  ChevronDown,
  Star,
  Calendar,
  Ruler,
  Zap,
  Fuel,
  Package,
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  Armchair,
  Square,
} from "lucide-react";

export function ClassifiedPreview({ formData, category }) {
  // State for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    reasonForSelling: false,
    description: false,
    story: false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Helper function to get images from the API data structure
  const getImages = () => {
    // Handle different possible image data structures
    if (formData.images && Array.isArray(formData.images)) {
      return formData.images.map((img) => ({
        id: img.id,
        url: img.url || img.image_url || img.src,
        isCover: img.isCover || img.is_cover || false,
      }));
    }

    // If coverPhoto exists, use it as the main image
    if (formData.coverPhoto) {
      return [
        {
          id: "cover",
          url: formData.coverPhoto,
          isCover: true,
        },
      ];
    }

    return [];
  };

  const images = getImages();

  // Find cover image
  const coverImage =
    images.find((img) => img.isCover)?.url ||
    (images.length > 0 ? images[0].url : null) ||
    formData.coverPhoto ||
    "/placeholder.svg?height=400&width=600";

  // Format price
  const formatPrice = (price) => {
    if (!price) return "$0";
    return `$${Number(price).toLocaleString()}`;
  };

  // Get category icon
  const getCategoryIcon = () => {
    switch (category.toLowerCase()) {
      case "house":
      case "rent or sell your property":
        return <Home className="h-5 w-5" />;
      case "auto":
        return <Car className="h-5 w-5" />;
      case "furniture":
        return <Sofa className="h-5 w-5" />;
      case "electronics":
        return <Tv2 className="h-5 w-5" />;
      case "appliances":
      case "appliance":
        return <Refrigerator className="h-5 w-5" />;
      case "apparels":
      case "apparel":
        return <Shirt className="h-5 w-5" />;
      case "roommate":
        return <Users className="h-5 w-5" />;
      case "sublease":
        return <Building2 className="h-5 w-5" />;
      default:
        return <Home className="h-5 w-5" />;
    }
  };

  // Helper function to convert details array to object for easier access
  const getSpecificDetails = () => {
    // If specific_details is already an object, use it (this is what we pass from the form)
    if (
      formData.specific_details &&
      typeof formData.specific_details === "object" &&
      !Array.isArray(formData.specific_details)
    ) {
      return formData.specific_details;
    }

    // If details is an array (from API), convert to object
    if (formData.details && Array.isArray(formData.details)) {
      const detailsObj = {};
      formData.details.forEach((detail) => {
        if (detail.key && detail.value !== undefined) {
          // Handle special cases for boolean and array values
          if (detail.key === "isUtilities" || detail.key === "isPreowned") {
            detailsObj[detail.key] =
              detail.value === "true" || detail.value === true;
          } else if (detail.key === "amenities") {
            detailsObj[detail.key] =
              typeof detail.value === "string"
                ? detail.value.split(",").filter(Boolean)
                : detail.value;
          } else if (
            detail.key === "condition" ||
            detail.key === "bedrooms" ||
            detail.key === "bathrooms" ||
            detail.key === "squareFoot" ||
            detail.key === "totalLeaseLength" ||
            detail.key === "remainingLease" ||
            detail.key === "kilometers"
          ) {
            detailsObj[detail.key] = Number(detail.value);
          } else {
            detailsObj[detail.key] = detail.value;
          }
        }
      });
      return detailsObj;
    }

    // Fallback to formData itself
    return formData;
  };

  const specificDetails = getSpecificDetails();

  // Render special fields section (step 3 details)
  const renderSpecialFields = () => {
    const fields = [];

    // Condition rating (common across many forms)
    if (specificDetails.condition) {
      fields.push({
        icon: <Star className="h-5 w-5 text-gray-500" />,
        label: "Condition",
        value: `${specificDetails.condition}/10`,
      });
    }

    // Property Type (House, Sublease)
    if (specificDetails.propertyType) {
      fields.push({
        icon: <Building2 className="h-5 w-5 text-gray-500" />,
        label: "Property Type",
        value:
          specificDetails.propertyType.charAt(0).toUpperCase() +
          specificDetails.propertyType.slice(1),
      });
    }

    // Square Footage (House, Sublease)
    if (specificDetails.squareFoot) {
      fields.push({
        icon: <Square className="h-5 w-5 text-gray-500" />,
        label: "Square Feet",
        value: `${Number(specificDetails.squareFoot).toLocaleString()} sq ft`,
      });
    }

    // Bedrooms (House, Sublease)
    if (specificDetails.bedrooms) {
      fields.push({
        icon: <BedDouble className="h-5 w-5 text-gray-500" />,
        label: "Bedrooms",
        value: specificDetails.bedrooms,
      });
    }

    // Bathrooms (House, Sublease)
    if (specificDetails.bathrooms) {
      fields.push({
        icon: <Bath className="h-5 w-5 text-gray-500" />,
        label: "Bathrooms",
        value: specificDetails.bathrooms,
      });
    }

    // Engine Type (Auto)
    if (specificDetails.engineType) {
      const engineIcon =
        specificDetails.engineType === "ev" ? (
          <Zap className="h-5 w-5 text-gray-500" />
        ) : (
          <Fuel className="h-5 w-5 text-gray-500" />
        );
      fields.push({
        icon: engineIcon,
        label: "Engine",
        value:
          specificDetails.engineType === "ev"
            ? "Electric"
            : specificDetails.engineType.charAt(0).toUpperCase() +
              specificDetails.engineType.slice(1),
      });
    }

    // Kilometers (Auto)
    if (specificDetails.kilometers) {
      fields.push({
        icon: <Ruler className="h-5 w-5 text-gray-500" />,
        label: "Kilometers",
        value: `${Number(specificDetails.kilometers).toLocaleString()} km`,
      });
    }

    // Apparel Type (Apparel)
    if (specificDetails.apparelType) {
      fields.push({
        icon: <Shirt className="h-5 w-5 text-gray-500" />,
        label: "Type",
        value:
          specificDetails.apparelType.charAt(0).toUpperCase() +
          specificDetails.apparelType.slice(1),
      });
    }

    // Size (Apparel)
    if (specificDetails.size) {
      fields.push({
        icon: <Package className="h-5 w-5 text-gray-500" />,
        label: "Size",
        value: specificDetails.size.toUpperCase(),
      });
    }

    // Model Release Year (Electronics, Appliance)
    if (specificDetails.modelReleaseYear) {
      fields.push({
        icon: <Calendar className="h-5 w-5 text-gray-500" />,
        label: "Year",
        value: specificDetails.modelReleaseYear,
      });
    }

    // Lease information (Sublease)
    if (specificDetails.totalLeaseLength && specificDetails.remainingLease) {
      fields.push({
        icon: <Clock className="h-5 w-5 text-gray-500" />,
        label: "Lease",
        value: `${specificDetails.remainingLease}/${specificDetails.totalLeaseLength} months`,
      });
    }

    // Agreement Type (Sublease)
    if (specificDetails.agreementType) {
      fields.push({
        icon: <Building2 className="h-5 w-5 text-gray-500" />,
        label: "Agreement",
        value:
          specificDetails.agreementType.charAt(0).toUpperCase() +
          specificDetails.agreementType.slice(1),
      });
    }

    // Utilities (Sublease)
    if (specificDetails.isUtilities !== undefined) {
      fields.push({
        icon: specificDetails.isUtilities ? (
          <CheckCircle className="h-5 w-5 text-gray-500" />
        ) : (
          <XCircle className="h-5 w-5 text-gray-500" />
        ),
        label: "Utilities",
        value: specificDetails.isUtilities ? "Included" : "Not Included",
      });
    }

    // Furnished Status (Sublease)
    if (specificDetails.furnished) {
      fields.push({
        icon: <Armchair className="h-5 w-5 text-gray-500" />,
        label: "Furnished",
        value:
          specificDetails.furnished.charAt(0).toUpperCase() +
          specificDetails.furnished.slice(1).replace("-", " "),
      });
    }

    // Gender Preference (Roommate)
    if (specificDetails.genderPreference) {
      fields.push({
        icon: <Users className="h-5 w-5 text-gray-500" />,
        label: "Gender Preference",
        value:
          specificDetails.genderPreference.charAt(0).toUpperCase() +
          specificDetails.genderPreference.slice(1).replace("-", " "),
      });
    }

    // Age Range (Roommate)
    if (specificDetails.ageRange) {
      fields.push({
        icon: <Calendar className="h-5 w-5 text-gray-500" />,
        label: "Age Range",
        value: specificDetails.ageRange,
      });
    }

    // Move-in Date (Roommate)
    if (specificDetails.moveInDate) {
      fields.push({
        icon: <Calendar className="h-5 w-5 text-gray-500" />,
        label: "Move-in Date",
        value: new Date(specificDetails.moveInDate).toLocaleDateString(),
      });
    }

    // Lease Duration (Roommate)
    if (specificDetails.leaseDuration) {
      fields.push({
        icon: <Clock className="h-5 w-5 text-gray-500" />,
        label: "Lease Duration",
        value:
          specificDetails.leaseDuration.charAt(0).toUpperCase() +
          specificDetails.leaseDuration.slice(1).replace("-", " "),
      });
    }

    // Security Deposit (Sublease)
    if (
      specificDetails.securityDeposit &&
      specificDetails.securityDeposit > 0
    ) {
      fields.push({
        icon: <DollarSign className="h-5 w-5 text-gray-500" />,
        label: "Security Deposit",
        value: `$${Number(specificDetails.securityDeposit).toLocaleString()}`,
      });
    }

    if (
      fields.length === 0 &&
      (!specificDetails.amenities || specificDetails.amenities.length === 0)
    ) {
      return (
        <div className="border-t border-b border-gray-200 py-6 my-6">
          <p className="text-gray-500 text-center">
            No additional details available
          </p>
        </div>
      );
    }

    return (
      <div className="border-t border-b border-gray-200 py-6 my-6">
        {fields.length > 0 && (
          <div className="flex flex-wrap justify-between gap-y-4">
            {fields.map((field, index) => (
              <div key={index} className="flex items-center gap-2 min-w-0">
                {field.icon}
                <div className="min-w-0">
                  <div className="text-xs text-gray-500 font-afacad uppercase tracking-wide">
                    {field.label}
                  </div>
                  <div className="text-sm font-medium text-gray-900 font-afacad truncate">
                    {field.value}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Amenities Section */}
        {specificDetails.amenities &&
          Array.isArray(specificDetails.amenities) &&
          specificDetails.amenities.length > 0 && (
            <div
              className={`${
                fields.length > 0 ? "mt-6 pt-6 border-t border-gray-200" : ""
              }`}
            >
              <h4 className="text-sm font-medium text-gray-700 mb-3 font-afacad uppercase tracking-wide">
                Amenities
              </h4>
              <div className="flex flex-wrap gap-2">
                {specificDetails.amenities.map((amenityId, index) => {
                  const amenityLabels = {
                    parking: "🚗 Parking",
                    wifi: "📶 Wi-Fi",
                    "air-conditioning": "❄️ Air Conditioning",
                    heating: "🔥 Heating",
                    "pet-friendly": "🐕 Pet-Friendly",
                    "laundry-in-unit": "👕 Laundry In-Unit",
                    "washer-dryer-on-site": "🔄 Washer/Dryer On-Site",
                    "balcony-patio": "🏠 Balcony/Patio",
                    fireplace: "🔥 Fireplace",
                    "fully-equipped-kitchen": "🍳 Fully Equipped Kitchen",
                    gym: "💪 Gym",
                    "gated-community": "🏘️ Gated Community",
                    "security-system": "🔒 Security System",
                  };

                  return (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {amenityLabels[amenityId] || amenityId}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
      </div>
    );
  };

  const renderCollapsibleSection = (key, title, content) => {
    if (!content) return null;

    const isExpanded = expandedSections[key];

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection(key)}
          className="w-full p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <span className="font-medium text-left font-fraunces">{title}</span>
          <ChevronDown
            className={`h-5 w-5 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </button>
        {isExpanded && (
          <div className="p-4 border-t border-gray-200">
            <p className="text-gray-700 leading-relaxed font-afacad">
              {content}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-bold font-fraunces">
          Here's How Your Listing Looks!
        </h2>
      </div>

      {/* Title and location */}
      <div className="p-6">
        <h1 className="text-3xl font-bold mb-3 leading-tight font-fraunces">
          {formData.title || `${category} Listing`}
        </h1>

        <div className="flex items-center gap-2 text-gray-600 mb-4">
          <span className="text-purple-700 text-sm font-semibold font-afacad">
            {category}
          </span>
          <span className="text-gray-400">•</span>
          <MapPin className="h-4 w-4" />
          <span className="text-sm font-afacad">
            {formData.addressLine1 || formData.addressLine2 || formData.state
              ? `${formData.addressLine1 || ""}${
                  formData.addressLine1 && formData.addressLine2 ? ", " : ""
                }${formData.addressLine2 || ""}${
                  (formData.addressLine1 || formData.addressLine2) &&
                  formData.state
                    ? ", "
                    : ""
                }${formData.state || ""}`
              : "Location not specified"}
          </span>
        </div>

        {/* Preowned/New status */}
        {specificDetails.isPreowned !== undefined && (
          <div className="mb-4">
            <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium font-afacad bg-black text-white">
              {specificDetails.isPreowned ? "Used" : "New"}
            </span>
          </div>
        )}

        {/* Price */}
        {formData.price && (
          <div className="mb-4">
            <span className="text-3xl font-bold text-purple-700 font-fraunces">
              {formatPrice(formData.price)}
            </span>
          </div>
        )}

        {/* Main content */}
        <div className="">
          {/* Cover image */}
          <div className="relative w-full h-[300px] md:h-[400px]">
            <img
              src={coverImage || "/placeholder.svg"}
              alt={formData.title || "Classified image"}
              className="w-full h-full object-cover rounded-lg"
              onError={(e) => {
                e.target.src = "/placeholder.svg?height=400&width=600";
              }}
            />
          </div>

          {/* Thumbnail gallery */}
          {images.length > 1 && (
            <div className="flex overflow-x-auto gap-3 p-0 pt-4 pb-4">
              {images.map((img, index) => (
                <div
                  key={img.id || index}
                  className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-gray-200"
                >
                  <img
                    src={img.url || "/placeholder.svg"}
                    alt={`Image ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "/placeholder.svg?height=80&width=80";
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Special Fields Section (Step 3 details) */}
          {renderSpecialFields()}
        </div>

        {/* Collapsible sections */}
        <div className="px-0 space-y-4">
          {renderCollapsibleSection(
            "reasonForSelling",
            "Reason For Selling",
            formData.reasonToSell
          )}

          {renderCollapsibleSection(
            "description",
            "More Description",
            formData.description
          )}

          {renderCollapsibleSection("story", "Seller's Story", formData.story)}
        </div>

        {/* Seller info */}
        <div className="p-0 mt-6">
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 font-afacad">
                Seller Information
              </h3>

              {/* Seller details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left column */}
                <div className="space-y-4">
                  {formData.fullName && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2 font-afacad uppercase tracking-wide">
                        Seller Name
                      </h4>
                      <p className="text-gray-900 font-medium text-base font-afacad">
                        {formData.fullName}
                      </p>
                    </div>
                  )}

                  {/* Contact details */}
                  <div className="space-y-3">
                    {formData.phoneNumber && (
                      <div className="flex items-center gap-3">
                        <Phone className="h-4 w-4 text-gray-500 flex-shrink-0" />
                        <span className="text-gray-900 font-afacad">
                          {formData.phoneNumber}
                        </span>
                      </div>
                    )}
                    {formData.email && (
                      <div className="flex items-center gap-3">
                        <Mail className="h-4 w-4 text-gray-500 flex-shrink-0" />
                        <span className="text-gray-900 font-afacad break-all">
                          {formData.email}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right column */}
                <div className="space-y-4">
                  {/* Property/Item address */}
                  {(formData.addressLine1 || formData.state) && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2 font-afacad uppercase tracking-wide">
                        Location
                      </h4>
                      <div className="text-gray-900 font-afacad space-y-1">
                        {formData.addressLine1 && (
                          <p>{formData.addressLine1}</p>
                        )}
                        {formData.addressLine2 && (
                          <p>{formData.addressLine2}</p>
                        )}
                        {formData.state && <p>{formData.state}</p>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contact actions */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:items-center sm:justify-between">
                <button className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-700 text-white font-medium hover:bg-purple-800 transition-colors rounded-lg font-afacad">
                  <Phone className="h-4 w-4" />
                  <span>Contact Seller</span>
                </button>

                <button className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors rounded-lg font-afacad">
                  <MapPin className="h-4 w-4" />
                  <span>View Location</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
